import React from 'react';
import { Phone, MapPin, Clock, Wrench, Paintbrush, ShieldCheck, CheckCircle, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import ContactForm from '../components/ContactForm';
import Testimonials from '../components/Testimonials';
import FAQ from '../components/FAQ';

export const Home = () => {
  const services = [
    {
      icon: ShieldCheck,
      title: 'Collision Repair',
      description: 'Expert collision repair services to restore your vehicle to its original condition with precision and care.',
      image: 'https://images.unsplash.com/photo-1625168678189-3cf5cc7c16d1?w=800&q=80',
      process: [
        'Initial damage assessment and estimate',
        'Disassembly and detailed inspection',
        'Frame straightening and body panel repair',
        'Quality control and final inspection'
      ]
    },
    {
      icon: Paintbrush,
      title: 'Auto Painting',
      description: 'Professional automotive painting services using high-quality materials for a flawless finish.',
      image: 'https://images.unsplash.com/photo-1635691035955-2763da0d6f19?w=800&q=80',
      process: [
        'Surface preparation and sanding',
        'Precise color matching technology',
        'Multi-layer paint application',
        'Clear coat protection and buffing'
      ]
    },
    {
      icon: Wrench,
      title: 'Dent Removal',
      description: 'Specialized dent removal techniques that restore your vehicle\'s body without compromising the original paint.',
      image: 'https://images.unsplash.com/photo-1615906655593-ad0386982a0f?w=800&q=80',
      process: [
        'Dent location and size evaluation',
        'Paintless dent repair technique',
        'Panel reshaping and smoothing',
        'Final inspection and touch-up'
      ]
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1551522435-b2347f669045?w=1920&q=80"
            alt="Professional Auto Body Shop"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/70"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
            Kingsway Auto Collision LTD
          </h1>
          <p className="text-xl md:text-2xl text-slate-200 mb-8 max-w-3xl mx-auto">
            Professional Auto Body Repair Services in Burnaby
          </p>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Expert collision repair, painting, and dent removal services you can trust
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              size="lg"
              className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6 transition-all duration-300 shadow-lg hover:shadow-xl"
              onClick={() => scrollToSection('contact')}
            >
              <FileText className="mr-2 h-5 w-5" />
              Get FREE Estimate
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-white/10 hover:bg-white/20 text-white border-2 border-white text-lg px-8 py-6 backdrop-blur-sm transition-all duration-300"
              onClick={() => scrollToSection('services')}
            >
              Our Services
            </Button>
          </div>
        </div>
      </section>

      {/* Quick Info Bar */}
      <section className="bg-slate-800 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="flex items-center justify-center gap-3 text-white">
              <Phone className="h-6 w-6 text-amber-500" />
              <div className="text-left">
                <p className="text-sm text-slate-400">Main Line</p>
                <a href="tel:6045680063" className="text-lg font-semibold hover:text-amber-500 transition-colors">
                  604-568-0063
                </a>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 text-white">
              <Phone className="h-6 w-6 text-amber-500" />
              <div className="text-left">
                <p className="text-sm text-slate-400">Cell</p>
                <a href="tel:6042032008" className="text-lg font-semibold hover:text-amber-500 transition-colors">
                  604-203-2008
                </a>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 text-white">
              <MapPin className="h-6 w-6 text-amber-500" />
              <div className="text-left">
                <p className="text-sm text-slate-400">Location</p>
                <p className="text-lg font-semibold">5368 Lane St, Burnaby BC</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Our Services
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Comprehensive auto body repair solutions with expert craftsmanship
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={index} 
                  className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-0 bg-white"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="bg-amber-600 p-2 rounded-lg">
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-white">{service.title}</h3>
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-slate-600 leading-relaxed mb-4">{service.description}</p>
                    
                    {/* Process Breakdown */}
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <h4 className="font-semibold text-slate-900 mb-3 text-sm uppercase tracking-wide">Our Process</h4>
                      <ul className="space-y-2">
                        {service.process.map((step, stepIndex) => (
                          <li key={stepIndex} className="flex items-start gap-2 text-sm text-slate-600">
                            <CheckCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <span>{step}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Free Estimate Button */}
                    <div className="mt-4">
                      <Button
                        onClick={() => scrollToSection('contact')}
                        className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold transition-all duration-300"
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        Get FREE Estimate
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                About Kingsway Auto Collision
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                At Kingsway Auto Collision LTD, we take pride in providing exceptional auto body repair services to the Burnaby community and surrounding areas. Our experienced team is dedicated to restoring your vehicle to its pre-accident condition with meticulous attention to detail.
              </p>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                We use state-of-the-art equipment and high-quality materials to ensure every repair meets the highest standards. Whether you need collision repair, professional painting, or dent removal, our skilled technicians have the expertise to handle it all.
              </p>
              <div className="grid grid-cols-2 gap-6 mt-8">
                <div className="text-center p-6 bg-slate-50 rounded-lg">
                  <ShieldCheck className="h-12 w-12 text-amber-600 mx-auto mb-3" />
                  <h3 className="font-bold text-slate-900 text-lg">Quality Work</h3>
                  <p className="text-slate-600 text-sm mt-2">Expert craftsmanship on every job</p>
                </div>
                <div className="text-center p-6 bg-slate-50 rounded-lg">
                  <Clock className="h-12 w-12 text-amber-600 mx-auto mb-3" />
                  <h3 className="font-bold text-slate-900 text-lg">Fast Service</h3>
                  <p className="text-slate-600 text-sm mt-2">Efficient repairs without compromise</p>
                </div>
              </div>

              <div className="mt-8">
                <Button
                  size="lg"
                  onClick={() => scrollToSection('contact')}
                  className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6"
                >
                  <FileText className="mr-2 h-5 w-5" />
                  Get Your FREE Estimate
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="relative h-[600px] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1632405862117-236585cfb757?w=800&q=80"
                  alt="Our Workshop"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <Testimonials />

      {/* FAQ Section */}
      <FAQ />

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Get In Touch</h2>
            <p className="text-xl text-slate-300">We're here to help with all your auto body repair needs</p>
          </div>

          {/* Contact Form */}
          <div className="mb-16">
            <ContactForm />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="bg-amber-600 p-4 rounded-lg">
                  <Phone className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Phone Numbers</h3>
                  <a href="tel:6045680063" className="text-lg text-slate-300 hover:text-amber-500 transition-colors block mb-1">
                    Main: 604-568-0063
                  </a>
                  <a href="tel:6042032008" className="text-lg text-slate-300 hover:text-amber-500 transition-colors block">
                    Cell: 604-203-2008
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-amber-600 p-4 rounded-lg">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Location</h3>
                  <p className="text-lg text-slate-300">5368 Lane Street</p>
                  <p className="text-lg text-slate-300">Burnaby, BC, Canada</p>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=5368+Lane+Street+Burnaby+BC+Canada"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-3 text-amber-500 hover:text-amber-400 transition-colors"
                  >
                    Get Directions →
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-amber-600 p-4 rounded-lg">
                  <Clock className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Business Hours</h3>
                  <p className="text-lg text-slate-300">Monday - Friday: 8:00 AM - 5:00 PM</p>
                  <p className="text-lg text-slate-300">Saturday: By Appointment</p>
                  <p className="text-lg text-slate-300">Sunday: Closed</p>
                </div>
              </div>
            </div>

            <div className="h-[400px] lg:h-full rounded-xl overflow-hidden shadow-2xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2605.7398748!2d-122.9849!3d49.2201!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x548676b0c8f6a0b5%3A0x9f9f9f9f9f9f9f9f!2s5368%20Lane%20St%2C%20Burnaby%2C%20BC!5e0!3m2!1sen!2sca!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Kingsway Auto Collision Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
